import type { JobLogRecord, WeekRecord } from "@/types/domain";

interface WeekFinancialsInputs {
  earned: number;
  bankAfter: number | null;
  stockAfter: number | null;
  totalInvested: number | null;
  bankBefore: number | null;
  stockBefore: number | null;
  totalInvestedBefore: number | null;
}

export interface WeekFinancials {
  earned: number;
  investedDiff: number | null;
  fluctuation: number | null;
  spent: number | null;
  totalChange: number | null;
}

export function computeWeekFinancials(
  input: WeekFinancialsInputs,
): WeekFinancials {
  const {
    earned,
    bankAfter,
    stockAfter,
    totalInvested,
    bankBefore,
    stockBefore,
    totalInvestedBefore,
  } = input;

  const investedDiff =
    totalInvested != null ? totalInvested - (totalInvestedBefore ?? 0) : null;

  const stockDelta =
    stockAfter != null ? stockAfter - (stockBefore ?? 0) : null;

  const fluctuation =
    stockDelta != null && investedDiff != null
      ? stockDelta - investedDiff
      : null;

  const bankDelta = bankAfter != null ? bankAfter - (bankBefore ?? 0) : null;

  const spent =
    bankDelta != null && investedDiff != null
      ? earned - investedDiff - bankDelta
      : null;

  const totalChange =
    bankDelta != null && stockDelta != null ? bankDelta + stockDelta : null;

  return { earned, investedDiff, fluctuation, spent, totalChange };
}

export function sumLogAmounts(logs: JobLogRecord[]): number {
  return logs.reduce((s, l) => s + l.data.amount, 0);
}

interface PriorWeekValues {
  bankBefore: number | null;
  stockBefore: number | null;
  totalInvestedBefore: number | null;
}

// Find the "before" reference values for a week by walking back through
// prior weeks (for the same user) and picking the most recent value of each
// field. Each field is looked up independently — they may come from
// different prior weeks.
export function findPriorValues(
  allWeeks: WeekRecord[],
  userId: string,
  isoWeek: number,
  year: number,
): PriorWeekValues {
  const priors = allWeeks
    .filter((w) => w.user_id === userId)
    .filter(
      (w) =>
        w.data.year < year ||
        (w.data.year === year && w.data.iso_week < isoWeek),
    )
    .sort((a, b) =>
      a.data.year !== b.data.year
        ? b.data.year - a.data.year
        : b.data.iso_week - a.data.iso_week,
    );

  let bankBefore: number | null = null;
  let stockBefore: number | null = null;
  let totalInvestedBefore: number | null = null;

  for (const w of priors) {
    if (bankBefore == null && w.data.bank_balance_after != null) {
      bankBefore = w.data.bank_balance_after;
    }
    if (stockBefore == null && w.data.stock_value_after != null) {
      stockBefore = w.data.stock_value_after;
    }
    if (totalInvestedBefore == null && w.data.total_invested != null) {
      totalInvestedBefore = w.data.total_invested;
    }
    if (
      bankBefore != null &&
      stockBefore != null &&
      totalInvestedBefore != null
    ) {
      break;
    }
  }

  return { bankBefore, stockBefore, totalInvestedBefore };
}
